<script setup lang="ts">
import { computed, onMounted } from 'vue'

import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import usePaymentSession from '@render/composables/usePaymentSession'
import { usePaymentData } from '@render/composables/usePaymentData'
import {
  recordLatestPaymentSession,
  recordCompletedPaymentSession,
} from '@render/composables/paymentSessionStore'
import { emitPayment, PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import { isPpoPurchased, recordPurchase } from '@render/composables/usePurchasedPpos'

import type { OfferGroupDataDto } from '@api/types.gen'
import type { SavedCardFields as Model } from './definition'

defineOptions({ inheritAttrs: false })

const fields = defineModel<Model>('fields', {
  required: true,
  default: (): Model => ({}),
})

const emit = defineEmits<{
  (e: 'onPaymentCompleted'): void
  (e: 'onPaymentFailed'): void
}>()

const pipe = usePipe()
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)
const isBrowser = typeof window !== 'undefined'
const { buildRealPaymentData } = usePaymentData(pipe, isBrowser)

const selectedOfferData = computed<OfferGroupDataDto | null>({
  get() {
    const key = fields.value.valueVariable?.key
    if (!key) return null

    return (pipe.getData(`var.${key}`) as OfferGroupDataDto | null) ?? null
  },
  set(val) {
    const key = fields.value.valueVariable?.key
    if (!key) return
    pipe.setData(`var.${key}`, val)
  },
})

// Precedence: `plans.options[0].offerGroup.key` (constant) wins over the
// runtime variable bound by `valueVariable.key`. The variable is used only
// when no plans-key is configured. `runCharge` syncs the resolved const back
// into the variable before charging so retries-after-failure see the same
// offergroup.
function resolveSavedCardOfferGroup(): OfferGroupDataDto | null {
  const offerGroupKey = fields.value.plans?.options[0]?.offerGroup?.key
  if (offerGroupKey) {
    const og = pipe.getData(`const.${offerGroupKey}`) as OfferGroupDataDto | null
    return og?.type === 'offer_group_value' ? og : null
  }
  return selectedOfferData.value ?? null
}

onMounted(() => {
  // User has reached a checkout component → VISIT_CHECKOUT (legacy parity:
  // /workspace/pipelines/lib/common/js/components/commonBasicPlanCheckout.js#L145).
  // The pipeorders integration translates this event into a PUBLIC
  // transitionOrder() POST. SavedCard does not collect new CC input, so it
  // does not emit payment:cc-submitted (TODO in usePaymentEvents.ts).
  emitPayment({ pipe, event: PAYMENT_EVENTS.checkoutVisited })

  if (selectedOfferData.value) return

  const option = fields.value.plans?.options[0]
  if (!option) return

  const offerGroupKey = option.offerGroup?.key
  if (!offerGroupKey) return

  const offerGroup = pipe.getData(`const.${offerGroupKey}`) as OfferGroupDataDto | null
  if (offerGroup && offerGroup.type === 'offer_group_value') {
    selectedOfferData.value = offerGroup
  }
})

async function runCharge() {
  if (isEditor.value) return

  // Resolve the offergroup this SavedCard should charge. Plans-key wins
  // over the runtime variable; variable is used only when no plans-key is
  // configured. The resolved offergroup drives every downstream side-effect
  // below (guard, sync-write, charge payload, analytics, recording) so a
  // mid-flow variable mutation can't introduce inconsistency.
  const offerGroup = resolveSavedCardOfferGroup()
  const isExitIntent = pipe.getData('var.global.hasExitIntentBeenAccepted')
  const ppoId =
    isExitIntent && offerGroup?.exit?.ppoId ? offerGroup.exit.ppoId : offerGroup?.regular?.ppoId

  if (isPpoPurchased(pipe, ppoId)) {
    // Already purchased this PPO (or its alternate from the same offergroup)
    // in the current pipe session — short-circuit to the schema-wired success
    // flow. We emit only the Vue component event; re-firing
    // PAYMENT_EVENTS.checkoutCompleted/Rendered would double-count analytics
    // for an already-completed transition.
    emit('onPaymentCompleted')
    return
  }

  // Sync the resolved offergroup into the bound variable so a charge failure
  // followed by a retry sees the same offergroup, and downstream consumers
  // (analytics, success-page render) see what was actually charged. When
  // plans-key took precedence, this overwrites whatever the variable held;
  // when the variable was the fallback source, it's a no-op-equivalent
  // rewrite of the same value.
  if (offerGroup) selectedOfferData.value = offerGroup

  const { fetchPaymentSession, data } = usePaymentSession(
    pipe.getData('const.global.paymentsApiUrl') as string,
    buildRealPaymentData({
      pipelineOfferType: fields.value.pipelineOfferType,
      priceProductObjectId: ppoId,
      restrictSaveCard: false,
    }),
  )

  await fetchPaymentSession()
  const checkoutSession = data.value
  // BE decides which provider (Primer/Yuno) actually charges a saved card —
  // record the raw response into the shared, provider-agnostic store so
  // consumers (e.g. GTM) can reuse its paymentSessionId without caring which.
  if (checkoutSession) recordLatestPaymentSession(pipe, checkoutSession)

  // Legacy parity (pipelines/lib/common/js/components/commonUpsell.js#L140):
  // INITIATE_CHECKOUT was tracked AFTER `createPrimerCheckoutSession` in the
  // saved-card / upsell flow, regardless of `instantlyCharged` outcome.
  // SUBMIT_CC_INPUT is intentionally NOT fired here — legacy never tracked it
  // for the saved-card path (no CC input was submitted).
  emitPayment({ pipe, event: PAYMENT_EVENTS.checkoutRendered, offer: offerGroup })

  if (checkoutSession?.instantlyCharged) {
    // SavedCard is a post-purchase upsell — this may not be the visitor's
    // first completed order in this pipe session, hence `completed.initial`
    // vs `.current` in the shared store (see paymentSessionStore.ts).
    recordCompletedPaymentSession(pipe, checkoutSession)
    recordPurchase(pipe, offerGroup, ppoId, fields.value.pipelineOfferType)
    emitPayment({
      pipe,
      event: PAYMENT_EVENTS.checkoutCompleted,
      offer: offerGroup,
      extra: { pipelineOfferType: fields.value.pipelineOfferType },
    })
    emit('onPaymentCompleted')
  } else {
    emitPayment({
      pipe,
      event: PAYMENT_EVENTS.checkoutFailed,
      offer: offerGroup,
      extra: {
        reason: 'saved_card_charge_failed',
        pipelineOfferType: fields.value.pipelineOfferType,
      },
    })
    emit('onPaymentFailed')
  }
}

useComponentProcessors({
  charge: () => runCharge(),
})
</script>

<!-- eslint-disable-next-line vue/valid-template-root -->
<template></template>
